import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import { has, isEqual, omit } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';
import {
  cloneDeep,
  compact,
  concat,
  filter,
  find,
  findIndex,
  first,
  flatMap,
  forEach,
  get,
  includes,
  isEmpty,
  isNil,
  map,
  reduce,
  slice,
  some,
} from 'lodash';
import { Row } from 'read-excel-file';
import { reduceExtraSpaces } from 'utils/helpers';

import type { ManifestCaseTarget, ManifestDraftCase, ManifestDraftIssue } from './dialogState';
import { NEW_CASE_HEADER } from './consts';
import {
  addCaseToRelevantList,
  getCaseBySlideId,
  getCaseFromRowCombinedWithRealData,
  getSlideIdentifierMismatchWarning,
  ManifestError,
  readExplicitNewCaseIntent,
  validateMandatoryHeaders,
} from './helpers';

export const unmatchedSlideIdentityMessage = (label: string) =>
  `${label} could not be matched. Correct or remove this slide from the manifest and upload again.`;

const getManifestRowMatchError = ({
  rowNumber,
  slideId,
  fileName,
}: {
  rowNumber: number;
  slideId?: string;
  fileName?: string;
}) => {
  if (fileName) {
    return `Error in row ${rowNumber}: ${unmatchedSlideIdentityMessage(`File name ${fileName}`)}`;
  }

  if (slideId) {
    return `Error in row ${rowNumber}: ${unmatchedSlideIdentityMessage(`Slide ID ${slideId}`)}`;
  }

  return `Error in row ${rowNumber}: ${unmatchedSlideIdentityMessage('Slide identifier')}`;
};

export interface ManifestWorkflowResolution {
  errorMessages: string[];
  warningMessages: string[];
  onlyManifestCases: Procedure[];
  resolvedCases: Procedure[];
  notIngestedSlides: Procedure[];
}

export interface UploadedManifestResolution {
  errorMessages: string[];
  warningMessages: string[];
  manifestCases: ManifestDraftCase[];
  notIngestedSlides: Procedure[];
  slideIdsToFetch: string[];
  explicitNewCaseSlideIds: string[];
  // Only present when the file repeats a slide; lets the user choose which row to keep instead of editing the file.
  duplicateSlideRows?: UploadedDuplicateSlideRow[];
}

export interface UploadedDuplicateSlideRow {
  slideKey: string;
  slideLabel: string;
  rowNumber: number;
  procedure: Procedure;
  explicitNewCase: boolean;
}

interface ResolveUploadedManifestRowsInput {
  editableExistingAndMockCases: Procedure[];
  notEditableSlidesMockCases: Procedure[];
  excelRows: Row[];
  procedureFieldContext: ProceduresFieldsContext;
  labId: string;
  studyId: string;
  preserveInvalidEditableValues?: boolean;
  preserveInvalidRows?: boolean;
  slideLookupComplete?: boolean;
  excludedRowNumbers?: number[];
}

const EDITABLE_CATALOG_FIELD_ERROR =
  /^(?:Error in row \d+: )?Invalid (?:Cancer Type|Biopsy Site|Stain Type|Biopsy Type|Preservation Method|Procedure Type)\. Choose a current option and upload again\.$/;

export const canOfferFixInCasePlan = (messages: string[]) =>
  messages.length > 0 &&
  messages.every((message) => EDITABLE_CATALOG_FIELD_ERROR.test(message) || /, On page \d+$/.test(message));

interface ResolveManifestWorkflowInput {
  existingProcedures: Procedure[];
  manualManifestCases: Procedure[];
  uploadedManifestCases: Procedure[];
  uploadedNotIngestedSlides: Procedure[];
  uploadedWarningMessages: string[];
  uploadedErrorMessages: string[];
  overrideCases: boolean;
}

export const getManifestCaseTargets = ({
  cases,
  existingProcedures,
  assignmentsBySlideId,
}: {
  cases: Procedure[];
  existingProcedures: Procedure[];
  assignmentsBySlideId: Record<string, ManifestCaseTarget>;
}): Record<string, ManifestCaseTarget> => {
  const targets = { ...assignmentsBySlideId };
  forEach(cases, (procedure) => {
    const isSavedCase =
      !get(procedure, 'isPendingSlideMockCase') &&
      !get(procedure, 'isMockCaseOfPendingSlide') &&
      (some(existingProcedures, { id: procedure.id }) || (procedure.id > 0 && Boolean(procedure.studyId)));
    forEach(procedure.slides, (slide) => {
      if (!has(targets, [slide.id])) {
        targets[slide.id] = isSavedCase ? procedure.id : 'new';
      }
    });
  });
  return targets;
};

export const resolveManifestExistingCaseAssignments = ({
  cases,
  existingProcedures,
  assignmentsBySlideId,
  casePatchesById,
  labId,
  studyId,
}: {
  cases: Procedure[];
  existingProcedures: Procedure[];
  assignmentsBySlideId: Record<string, ManifestCaseTarget>;
  casePatchesById: Record<number, Partial<CaseUpdate>>;
  labId: string;
  studyId: string;
}): { cases: Procedure[]; missingTargetCount: number } => {
  const resolvedCases: Procedure[] = [];
  const targetCasesById = new Map<number, Procedure>();
  let missingTargetCount = 0;

  forEach(cases, (procedure) => {
    const retainedSlides = filter(
      procedure.slides,
      (slide) => !has(assignmentsBySlideId, [slide.id]) || assignmentsBySlideId[slide.id] === 'new'
    );
    if (!isEmpty(retainedSlides)) {
      const retainedCase = { ...procedure, slides: map(retainedSlides, (slide) => ({ ...slide })) };
      resolvedCases.push(retainedCase);
      if (some(existingProcedures, { id: procedure.id })) targetCasesById.set(procedure.id, retainedCase);
    }
  });

  forEach(cases, (procedure) => {
    const unresolvedSlides: Slide[] = [];
    forEach(procedure.slides, (slide) => {
      if (!has(assignmentsBySlideId, [slide.id])) return;
      const targetCaseId = assignmentsBySlideId[slide.id];
      if (targetCaseId === 'new') return;
      const existingProcedure = find(
        existingProcedures,
        (candidate) =>
          candidate.id === targetCaseId &&
          (!candidate.labId || candidate.labId === labId) &&
          (!candidate.studyId || candidate.studyId === studyId)
      );
      if (!existingProcedure) {
        missingTargetCount += 1;
        unresolvedSlides.push({ ...slide });
        return;
      }

      const resolvedCase = targetCasesById.get(existingProcedure.id);
      if (resolvedCase) {
        if (!some(resolvedCase.slides, { id: slide.id })) resolvedCase.slides?.push({ ...slide });
      } else {
        const assignedCase = {
          ...existingProcedure,
          ...omit(casePatchesById[existingProcedure.id], ['label']),
          slides: [{ ...slide }],
        };
        targetCasesById.set(existingProcedure.id, assignedCase);
        resolvedCases.push(assignedCase);
      }
    });
    if (!isEmpty(unresolvedSlides)) resolvedCases.push({ ...procedure, slides: unresolvedSlides });
  });

  return { cases: resolvedCases, missingTargetCount };
};

export const collectManifestCaseLabelErrors = (cases: Procedure[], existingProcedures: Procedure[]) => {
  const existingCaseIds = new Set(map(existingProcedures, 'id'));
  const existingIdsByLabel = new Map<string, Set<number>>();
  const assignmentIdsByLabel = new Map<string, Set<number>>();

  forEach(existingProcedures, (procedure) => {
    if (!procedure.label) return;
    const caseIds = existingIdsByLabel.get(procedure.label) || new Set<number>();
    caseIds.add(procedure.id);
    existingIdsByLabel.set(procedure.label, caseIds);
  });

  const conflicts = new Set<string>();
  forEach(cases, (procedure) => {
    if (!procedure.label) return;
    const existingIds = existingIdsByLabel.get(procedure.label);
    const isExistingCase = !get(procedure, 'isMockCaseOfPendingSlide') && existingCaseIds.has(procedure.id);
    if (existingIds && existingIds.size > 1) {
      conflicts.add(
        `Case label "${procedure.label}" is already used by ${existingIds.size} cases in this study. Choose one of those cases, or use a different label.`
      );
    } else if (isExistingCase && existingIds && !existingIds.has(procedure.id)) {
      conflicts.add(
        `Case label "${procedure.label}" belongs to a different case in this study. Choose that case, or use a different label.`
      );
    }
    if (isExistingCase && !(existingIds && existingIds.size > 1)) {
      const assignmentIds = assignmentIdsByLabel.get(procedure.label) || new Set<number>();
      assignmentIds.add(procedure.id);
      assignmentIdsByLabel.set(procedure.label, assignmentIds);
      if (assignmentIds.size > 1) {
        conflicts.add(
          `Case label "${procedure.label}" is used by more than one case in this assignment. Choose one case, or use a different label.`
        );
      }
    }
  });

  return [...conflicts];
};

export const joinNewTargetsToUniqueExistingCases = ({
  cases,
  existingProcedures,
  targetsBySlideId,
}: {
  cases: Procedure[];
  existingProcedures: Procedure[];
  targetsBySlideId: Record<string, ManifestCaseTarget>;
}): Record<string, ManifestCaseTarget> => {
  const existingIdsByLabel = new Map<string, number[]>();
  forEach(existingProcedures, (procedure) => {
    if (!procedure.label) return;
    const caseIds = existingIdsByLabel.get(procedure.label) || [];
    caseIds.push(procedure.id);
    existingIdsByLabel.set(procedure.label, caseIds);
  });
  const joinedTargets = { ...targetsBySlideId };
  forEach(cases, (procedure) => {
    if (!procedure.label) return;
    const matchingCaseIds = existingIdsByLabel.get(procedure.label);
    if (matchingCaseIds?.length !== 1) return;
    forEach(procedure.slides, (slide) => {
      if (joinedTargets[slide.id] === 'new') joinedTargets[slide.id] = matchingCaseIds[0];
    });
  });
  return joinedTargets;
};

export const collectExplicitNewCaseLabelErrors = (
  cases: Procedure[],
  existingProcedures: Procedure[],
  targetsBySlideId: Readonly<Record<string, ManifestCaseTarget | undefined>>
) => {
  const existingIdsByLabel = new Map<string, number[]>();
  forEach(existingProcedures, (procedure) => {
    if (!procedure.label) return;
    const caseIds = existingIdsByLabel.get(procedure.label) || [];
    caseIds.push(procedure.id);
    existingIdsByLabel.set(procedure.label, caseIds);
  });
  return flatMap(cases, (procedure) => {
    if (!procedure.label) return [];
    const matchingCaseIds = existingIdsByLabel.get(procedure.label) || [];
    if (matchingCaseIds.length === 1) {
      const collidingSlides = filter(procedure.slides, (slide) => targetsBySlideId[slide.id] === 'new');
      if (isEmpty(collidingSlides)) return [];
      return [
        `Case label "${procedure.label}" matches a saved case in this study. Choose that case, or use a different label.`,
      ];
    }
    if (matchingCaseIds.length < 2) return [];
    const collidingSlides = filter(procedure.slides, (slide) => targetsBySlideId[slide.id] === 'new');
    if (isEmpty(collidingSlides)) return [];
    return [
      `Case label "${procedure.label}" is used by ${matchingCaseIds.length} cases. Choose which case these slides join.`,
    ];
  });
};

export const collectExplicitNewCaseCollisionSlideIds = (
  cases: Procedure[],
  existingProcedures: Procedure[],
  targetsBySlideId: Readonly<Record<string, ManifestCaseTarget | undefined>>
) => {
  const savedLabels = new Set(compact(map(existingProcedures, 'label')));
  return flatMap(cases, (procedure) => {
    if (!procedure.label || !savedLabels.has(procedure.label)) return [];
    return map(
      filter(procedure.slides, (slide) => targetsBySlideId[slide.id] === 'new'),
      'id'
    );
  });
};

const DUPLICATE_SLIDE_ROW_ERROR_LIMIT = 10;
const UPLOAD_DUPLICATE_GUIDANCE = 'Choose one of these rows. The file does not need to be edited.';

// One slide must never have two rows in the manifest, whatever source added them. Each list is checked on its own,
// so duplicates that a later merge step would silently collapse are still reported.
export const collectDuplicateSlideRowErrors = (...caseLists: Procedure[][]) => {
  const duplicateNamesById = new Map<string, string>();
  forEach(caseLists, (cases) => {
    const seenSlideIds = new Set<string>();
    forEach(cases, (procedure) => {
      forEach(procedure.slides, (slide) => {
        if (seenSlideIds.has(slide.id)) duplicateNamesById.set(slide.id, slide.originalFileName || slide.id);
        seenSlideIds.add(slide.id);
      });
    });
  });
  const duplicates = [...duplicateNamesById.values()];
  const errors = map(
    slice(duplicates, 0, DUPLICATE_SLIDE_ROW_ERROR_LIMIT),
    (name) =>
      `Slide ${name} appears in more than one row of the manifest. Remove the slide from the manifest, then add it back once so only one row remains.`
  );
  if (duplicates.length > DUPLICATE_SLIDE_ROW_ERROR_LIMIT) {
    errors.push(
      `${
        duplicates.length - DUPLICATE_SLIDE_ROW_ERROR_LIMIT
      } more slides have duplicate rows in the manifest. Keep one row per slide.`
    );
  }
  return errors;
};

const getUploadedSlideIdentity = (slide: Slide | undefined, isFetchPending: boolean, knownCases: Procedure[]) => {
  if (!slide?.id) return null;
  if (isFetchPending || getCaseBySlideId(slide.id, knownCases)) {
    return { key: `id:${slide.id}`, label: `Slide ID ${slide.id}` };
  }
  // A row that matched no slide carries a placeholder id, so its file name is its identity.
  const fileName = reduceExtraSpaces(slide.originalFileName || '');
  return fileName ? { key: `file:${fileName}`, label: `File name ${fileName}` } : null;
};

const CASE_SHARED_METADATA_ERROR_LIMIT = 10;

const sharedMetadataValue = (value: unknown, emptyValue: unknown = null) => (value == null ? emptyValue : value);

const analyzeCaseSharedMetadata = (cases: Procedure[]) => {
  const firstValuesByLabel = new Map<
    string,
    { cancerTypeId: unknown; cancerSubtypes: unknown; biopsySiteId: unknown; biopsyType: unknown }
  >();
  const reportedConflicts = new Set<string>();
  const conflictingCaseLabels = new Set<string>();
  const errors: string[] = [];
  let omittedConflictCount = 0;

  const reportConflict = (label: string, field: string) => {
    conflictingCaseLabels.add(label);
    const conflictKey = `${label}\0${field}`;
    if (reportedConflicts.has(conflictKey)) return;
    reportedConflicts.add(conflictKey);
    if (errors.length < CASE_SHARED_METADATA_ERROR_LIMIT) {
      errors.push(
        `${field} conflicts across slides in case ${label}. Use the same value for every slide in this case.`
      );
      return;
    }
    omittedConflictCount += 1;
  };

  forEach(cases, (procedure) => {
    if (!procedure.label) return;
    forEach(procedure.slides, (slide) => {
      const currentValues = {
        cancerTypeId: sharedMetadataValue(procedure.cancerTypeId),
        cancerSubtypes: sharedMetadataValue(procedure.cancerSubtypes, []),
        biopsySiteId: sharedMetadataValue(slide.biopsySiteId),
        biopsyType: sharedMetadataValue(slide.biopsyType),
      };
      const firstValues = firstValuesByLabel.get(procedure.label);
      if (!firstValues) {
        firstValuesByLabel.set(procedure.label, currentValues);
        return;
      }
      if (!isEqual(currentValues.cancerTypeId, firstValues.cancerTypeId))
        reportConflict(procedure.label, 'Cancer Type');
      if (!isEqual(currentValues.cancerSubtypes, firstValues.cancerSubtypes)) {
        reportConflict(procedure.label, 'Cancer Subtype');
      }
      if (!isEqual(currentValues.biopsySiteId, firstValues.biopsySiteId))
        reportConflict(procedure.label, 'Biopsy Site');
      if (!isEqual(currentValues.biopsyType, firstValues.biopsyType)) reportConflict(procedure.label, 'Biopsy Type');
    });
  });

  if (omittedConflictCount > 0) {
    errors.push(
      omittedConflictCount === 1
        ? '1 more case metadata conflict needs the same value on every slide in that case.'
        : `${omittedConflictCount} more case metadata conflicts need the same value on every slide in that case.`
    );
  }

  return { errors, conflictingCaseLabels };
};

export const collectCaseSharedMetadataErrors = (cases: Procedure[]) => analyzeCaseSharedMetadata(cases).errors;

// Saved cases are keyed by ID rather than label: two saved cases may share a label and must stay separate rows.
export const addSavedCasesToDraft = (draftCases: Procedure[], savedCases: Procedure[]) => {
  const draftSlideIds = new Set(flatMap(draftCases, (draftCase) => map(draftCase.slides, 'id')));

  return reduce(
    savedCases,
    (cases, savedCase) => {
      const slidesToAdd = filter(savedCase.slides, (slide) => !draftSlideIds.has(slide.id));
      if (isEmpty(slidesToAdd)) return cases;

      const draftCaseIndex = findIndex(cases, { id: savedCase.id });
      if (draftCaseIndex === -1) {
        return [...cases, { ...cloneDeep(savedCase), slides: cloneDeep(slidesToAdd) }];
      }

      return map(cases, (draftCase, index) =>
        index === draftCaseIndex
          ? { ...draftCase, slides: [...(draftCase.slides || []), ...cloneDeep(slidesToAdd)] }
          : draftCase
      );
    },
    cloneDeep(draftCases)
  );
};

export const mergeManifestCases = (baseCases: Procedure[], casesToAdd: Procedure[]) => {
  return reduce(
    casesToAdd,
    (cases, caseToAdd) => {
      const slideToAdd = first(caseToAdd.slides);
      if (!slideToAdd) {
        return cases;
      }

      // Blank labels are placeholders awaiting user input, so they never identify the same case.
      const existingCaseIndex = caseToAdd.label ? findIndex(cases, { label: caseToAdd.label }) : -1;

      if (existingCaseIndex === -1) {
        return [...cases, cloneDeep(caseToAdd)];
      }

      const existingCase = cloneDeep(cases[existingCaseIndex]);
      const slidesToAdd = filter(
        caseToAdd.slides,
        (incomingSlide) => !some(existingCase.slides, (existingSlide) => existingSlide.id === incomingSlide.id)
      );

      if (isEmpty(slidesToAdd)) {
        return cases;
      }

      existingCase.slides = [...(existingCase.slides || []), ...cloneDeep(slidesToAdd)];

      return [...slice(cases, 0, existingCaseIndex), existingCase, ...slice(cases, existingCaseIndex + 1)];
    },
    cloneDeep(baseCases)
  );
};

export const combineManifestCasesWithExistingCases = (
  manifestCases: Procedure[],
  existingProcedures: Procedure[],
  isExcludedFromExistingCaseMatch: (manifestCase: Procedure) => boolean = () => false
): { cases: Procedure[]; warningMessages: string[] } => {
  if (isEmpty(manifestCases)) {
    return { cases: [], warningMessages: [] };
  }

  const warningMessages: string[] = [];
  const combinedCases = cloneDeep(manifestCases);
  const manifestSlideIds = map(flatMap(combinedCases, 'slides'), 'id');

  forEach(combinedCases, (manifestCase) => {
    const existingCase = isExcludedFromExistingCaseMatch(manifestCase)
      ? undefined
      : find(existingProcedures, { label: manifestCase.label });

    if (!existingCase) {
      return;
    }

    const slidesToAdd = filter(
      existingCase.slides || [],
      (existingSlide) => !includes(map(manifestCase.slides, 'id'), existingSlide.id)
    );

    forEach(slidesToAdd, (slideToAdd) => {
      if (includes(manifestSlideIds, slideToAdd.id)) {
        warningMessages.push(
          `Slide id ${slideToAdd.id} found also in Case ${manifestCase.label}. Moving the slide from the old case to the new case.`
        );
      }
    });

    manifestCase.slides = concat(
      manifestCase.slides || [],
      filter(slidesToAdd, (slide) => !includes(manifestSlideIds, slide.id))
    );
  });

  // A draft case excluded from matching keeps its saved namesake visible with the slides that stay in it.
  const retainedSavedCases: Procedure[] = [];
  forEach(combinedCases, (manifestCase) => {
    if (!isExcludedFromExistingCaseMatch(manifestCase)) return;
    const savedCase = find(existingProcedures, { label: manifestCase.label });
    const isRepresented =
      !savedCase ||
      some(
        combinedCases,
        (candidate) => !isExcludedFromExistingCaseMatch(candidate) && candidate.id === savedCase.id
      ) ||
      some(retainedSavedCases, { id: savedCase.id });
    const remainingSlides = filter(savedCase?.slides, (slide) => !includes(manifestSlideIds, slide.id));
    if (isRepresented || isEmpty(remainingSlides)) return;
    retainedSavedCases.push({ ...cloneDeep(savedCase), slides: remainingSlides });
  });

  return { cases: concat(combinedCases, retainedSavedCases), warningMessages };
};

export const applyManifestDraftPatches = (
  baseCases: ManifestDraftCase[],
  casePatchesById: Record<number, Partial<CaseUpdate>>,
  slidePatchesById: Record<string, Partial<SlideUpdate>>
) => {
  return map(baseCases, (baseCase) => ({
    ...baseCase,
    ...(casePatchesById[baseCase.id] || {}),
    ...(baseCase.draftIssues && {
      draftIssues: baseCase.draftIssues.filter(
        (issue) => !baseCase.slides?.some((slide) => has(slidePatchesById, [slide.id, issue.field]))
      ),
    }),
    slides: map(baseCase.slides, (slide) => ({
      ...slide,
      ...(slidePatchesById[slide.id] || {}),
    })),
  }));
};

export const removeSlidesFromManifestCases = (baseCases: ManifestDraftCase[], removedSlideIds: string[]) => {
  if (isEmpty(removedSlideIds)) {
    return cloneDeep(baseCases);
  }

  return compact(
    map(baseCases, (baseCase) => {
      const slides = filter(baseCase.slides, (slide) => !includes(removedSlideIds, slide.id));

      if (isEmpty(slides)) {
        return null;
      }

      return {
        ...cloneDeep(baseCase),
        slides,
      };
    })
  );
};

export const resolveUploadedManifestRows = ({
  editableExistingAndMockCases,
  notEditableSlidesMockCases,
  excelRows,
  procedureFieldContext,
  labId,
  studyId,
  preserveInvalidEditableValues = false,
  preserveInvalidRows = false,
  slideLookupComplete = false,
  excludedRowNumbers = [],
}: ResolveUploadedManifestRowsInput): UploadedManifestResolution => {
  const warningMessages: string[] = [];
  const duplicateRowErrors: string[] = [];
  const duplicateRowsByKey = new Map<string, UploadedDuplicateSlideRow[]>();
  const excludedRows = new Set(excludedRowNumbers);
  let isAborted = false;
  let duplicateRowCount = 0;
  let manifestCases: ManifestDraftCase[] = [];
  let notIngestedSlides: Procedure[] = [];
  let slideIdsToFetch: string[] = [];
  let explicitNewCaseSlideIds: string[] = [];
  let errorMessages: string[] = [];

  if (isEmpty(excelRows) || excelRows.length === 1) {
    errorMessages = ['No data found in the file'];
  } else if (!isEmpty(excelRows)) {
    const fileHeaders = map(first(excelRows), (header) => String(header).toLowerCase());

    try {
      validateMandatoryHeaders(fileHeaders);

      const allCases = concat(editableExistingAndMockCases, notEditableSlidesMockCases);
      const firstRowBySlideId = new Map<string, UploadedDuplicateSlideRow>();
      const firstRowByCaseLabel = new Map<string, { procedure: Procedure; rowNumber: number }>();

      forEach(slice(excelRows, 1), (row: Row, index) => {
        let newCase: ManifestDraftCase;
        const rowIssues: ManifestDraftIssue[] = [];
        const rowNumber = index + 1;
        if (excludedRows.has(rowNumber)) return;

        let explicitNewCase = false;
        try {
          try {
            explicitNewCase = readExplicitNewCaseIntent(fileHeaders, row);
          } catch (error) {
            if (!(error instanceof ManifestError) || !preserveInvalidRows) throw error;
            rowIssues.push({
              field: 'targetCaseAssignment',
              message: error.message,
              rawValue: String(
                row[
                  fileHeaders.findIndex((header) => header === NEW_CASE_HEADER.toLowerCase() || header === 'new case')
                ] ?? ''
              ),
            });
          }
          newCase = getCaseFromRowCombinedWithRealData(
            procedureFieldContext,
            fileHeaders,
            row,
            labId,
            studyId,
            index,
            editableExistingAndMockCases,
            notEditableSlidesMockCases,
            preserveInvalidEditableValues || preserveInvalidRows,
            preserveInvalidRows ? (issue) => rowIssues.push(issue) : undefined
          );
        } catch (error) {
          if (error instanceof ManifestError) {
            throw new ManifestError(`Error in row ${rowNumber}: ${error.message}`);
          }

          throw error;
        }

        const currentSlide = first(newCase.slides);
        const isFetchPending = preserveInvalidRows
          ? Boolean(row[fileHeaders.indexOf('slide id')]) && !getCaseBySlideId(currentSlide?.id, allCases)
          : includes(fileHeaders, 'slide id') && isNil(currentSlide?.originalFileName) && Boolean(currentSlide?.id);
        const slideIdentity = getUploadedSlideIdentity(currentSlide, isFetchPending, allCases);
        if (slideIdentity) {
          const firstRow = firstRowBySlideId.get(slideIdentity.key);
          const uploadedRow = {
            slideKey: slideIdentity.key,
            slideLabel: slideIdentity.label,
            rowNumber,
            procedure: newCase,
            explicitNewCase,
          };
          if (firstRow !== undefined) {
            duplicateRowCount += 1;
            if (duplicateRowErrors.length < 10) {
              duplicateRowErrors.push(
                `Error in row ${rowNumber}: ${slideIdentity.label} also appears in row ${firstRow.rowNumber}. ${UPLOAD_DUPLICATE_GUIDANCE}`
              );
            }
            duplicateRowsByKey.set(slideIdentity.key, [
              ...(duplicateRowsByKey.get(slideIdentity.key) ?? [firstRow]),
              uploadedRow,
            ]);
            if (!preserveInvalidRows) return;
          }
          firstRowBySlideId.set(slideIdentity.key, uploadedRow);
        }

        if (isFetchPending) {
          if (!slideLookupComplete) slideIdsToFetch.push(currentSlide.id);
          if (!preserveInvalidRows) return;
        }

        if (preserveInvalidRows) {
          if (!getCaseBySlideId(currentSlide?.id, allCases) && (!isFetchPending || slideLookupComplete)) {
            if (!rowIssues.some((issue) => issue.field === 'slideId')) {
              rowIssues.push({
                field: 'slideId',
                message: 'Incorrect slide. Remove or replace this row.',
              });
            }
          }
          newCase = {
            ...newCase,
            draftIssues: rowIssues,
            ...(explicitNewCase && { draftTarget: 'new' }),
            ...(!getCaseBySlideId(currentSlide?.id, allCases) && {
              draftIdentifier: {
                slideId: String(row[fileHeaders.indexOf('slide id')] ?? '') || null,
                fileName: String(row[fileHeaders.indexOf('file name')] ?? '') || null,
              },
            }),
          };
          manifestCases.push(newCase);
          if (currentSlide?.id && explicitNewCase) explicitNewCaseSlideIds.push(currentSlide.id);
          const warning = getSlideIdentifierMismatchWarning(fileHeaders, row, currentSlide, rowNumber);
          if (warning) warningMessages.push(warning);
          return;
        }

        const identifierMismatchWarning = getSlideIdentifierMismatchWarning(fileHeaders, row, currentSlide, rowNumber);
        if (identifierMismatchWarning) warningMessages.push(identifierMismatchWarning);

        if (currentSlide?.id && explicitNewCase) explicitNewCaseSlideIds.push(currentSlide.id);

        if (newCase.label) {
          const firstCaseRow = firstRowByCaseLabel.get(newCase.label);
          if (firstCaseRow) {
            const firstSlide = first(firstCaseRow.procedure.slides);
            const sharedFields = [
              ['Cancer Type', newCase.cancerTypeId ?? null, firstCaseRow.procedure.cancerTypeId ?? null],
              ['Cancer Subtype', newCase.cancerSubtypes ?? [], firstCaseRow.procedure.cancerSubtypes ?? []],
              ['Biopsy Site', currentSlide?.biopsySiteId ?? null, firstSlide?.biopsySiteId ?? null],
              ['Biopsy Type', currentSlide?.biopsyType ?? null, firstSlide?.biopsyType ?? null],
            ];
            const conflict = find(sharedFields, ([, incomingValue, firstValue]) => !isEqual(incomingValue, firstValue));
            if (conflict) {
              throw new ManifestError(
                `Error in row ${rowNumber}: ${conflict[0]} conflicts with row ${firstCaseRow.rowNumber} for the same case label. Use the same value across this case's slides and upload again.`
              );
            }
          } else {
            firstRowByCaseLabel.set(newCase.label, { procedure: newCase, rowNumber });
          }
        }

        const previousMissingSlideCount = flatMap(notIngestedSlides, 'slides').length;
        const nextResult = addCaseToRelevantList(newCase, notIngestedSlides, manifestCases, allCases);
        manifestCases = nextResult.updatedCases;
        notIngestedSlides = nextResult.updatedMissingSlides;

        if (flatMap(nextResult.updatedMissingSlides, 'slides').length > previousMissingSlideCount) {
          errorMessages.push(
            getManifestRowMatchError({ rowNumber, slideId: currentSlide?.id, fileName: currentSlide?.originalFileName })
          );
        }

        if (nextResult.warning) {
          warningMessages.push(nextResult.warning);
        }
      });
    } catch (error) {
      if (error instanceof ManifestError) {
        isAborted = true;
        errorMessages = [error.message];
        manifestCases = [];
        notIngestedSlides = [];
        slideIdsToFetch = [];
        explicitNewCaseSlideIds = [];
      } else {
        throw error;
      }
    }
  }

  if (duplicateRowCount > 0 && !preserveInvalidRows) {
    return {
      errorMessages: concat(
        duplicateRowErrors,
        duplicateRowCount > duplicateRowErrors.length
          ? [
              `${
                duplicateRowCount - duplicateRowErrors.length
              } additional slides are listed on more than one row. Choose one row for each.`,
            ]
          : []
      ),
      warningMessages: [],
      manifestCases: [],
      notIngestedSlides: [],
      slideIdsToFetch: [],
      explicitNewCaseSlideIds: [],
      ...(isAborted ? {} : { duplicateSlideRows: [...duplicateRowsByKey.values()].flat() }),
    };
  }

  return {
    errorMessages,
    warningMessages,
    manifestCases,
    notIngestedSlides,
    slideIdsToFetch: errorMessages.length > 0 ? [] : slideIdsToFetch,
    explicitNewCaseSlideIds: errorMessages.length > 0 ? [] : explicitNewCaseSlideIds,
  };
};

export const resolveManifestWorkflow = ({
  existingProcedures,
  manualManifestCases,
  uploadedManifestCases,
  uploadedNotIngestedSlides,
  uploadedWarningMessages,
  uploadedErrorMessages,
  overrideCases,
}: ResolveManifestWorkflowInput): ManifestWorkflowResolution => {
  const unmergedCases = concat(uploadedManifestCases, manualManifestCases);
  const hasLabelConflicts = !isEmpty(collectManifestCaseLabelErrors(unmergedCases, existingProcedures));
  const onlyManifestCases = hasLabelConflicts
    ? cloneDeep(unmergedCases)
    : mergeManifestCases(uploadedManifestCases, manualManifestCases);

  const combinedCases =
    overrideCases || hasLabelConflicts
      ? { cases: cloneDeep(onlyManifestCases), warningMessages: [] }
      : combineManifestCasesWithExistingCases(onlyManifestCases, existingProcedures);

  return {
    errorMessages: uploadedErrorMessages,
    warningMessages: [...uploadedWarningMessages, ...combinedCases.warningMessages],
    onlyManifestCases,
    resolvedCases: combinedCases.cases,
    notIngestedSlides: uploadedNotIngestedSlides,
  };
};

export const filterCasesWithErrors = ({
  cases,
  fields,
  procedureFieldContext,
  additionalIssueSlideIds = [],
}: {
  cases: Procedure[];
  fields: any[];
  procedureFieldContext: ProceduresFieldsContext;
  additionalIssueSlideIds?: readonly string[];
}) => {
  const caseIdsWithError = new Set<number>();
  const slideIdsWithError = new Set<string>();
  const { conflictingCaseLabels } = analyzeCaseSharedMetadata(cases);
  const additionalIssueIds = new Set(additionalIssueSlideIds);
  const countsBySlideId = new Map<string, number>();
  for (const slide of flatMap(cases, 'slides')) {
    countsBySlideId.set(slide.id, (countsBySlideId.get(slide.id) || 0) + 1);
  }

  forEach(cases, (procedure) => {
    forEach(procedure.slides, (slide) => {
      const row = {
        ...procedure,
        ...slide,
        slides: [slide],
        _unwoundRowMetadata: {
          baseRowId: procedure.id,
          innerRowId: slide.id,
        },
      };

      const hasError =
        additionalIssueIds.has(slide.id) ||
        conflictingCaseLabels.has(procedure.label) ||
        get(procedure, 'draftIssues.length', 0) > 0 ||
        (countsBySlideId.get(slide.id) || 0) > 1 ||
        some(fields, (field) =>
          field?.getError?.({ value: get(row, field.dataKey), row, context: procedureFieldContext })
        );

      if (hasError) {
        caseIdsWithError.add(procedure.id);
        slideIdsWithError.add(slide.id);
      }
    });
  });

  return map(
    filter(cases, (procedure) => caseIdsWithError.has(procedure.id)),
    (procedure) => ({
      ...procedure,
      slides: filter(procedure.slides, (slide) => slideIdsWithError.has(slide.id)),
    })
  );
};

export const collectManifestValidationErrors = ({
  cases,
  fields,
  procedureFieldContext,
  rowsPerPage,
}: {
  cases: Procedure[];
  fields: any[];
  procedureFieldContext: ProceduresFieldsContext;
  rowsPerPage: number;
}) => {
  const validationErrors: string[] = [];
  let rowIndex = 0;
  let unmatchedRowCount = 0;
  let invalidCellRowCount = 0;

  forEach(cases, (procedure: ManifestDraftCase) => {
    const rowCount = procedure.slides?.length || 0;
    if (procedure.draftIssues?.some((issue) => issue.field === 'slideId')) unmatchedRowCount += rowCount;
    if (procedure.draftIssues?.some((issue) => issue.field !== 'slideId')) invalidCellRowCount += rowCount;
    forEach(procedure.slides, (slide) => {
      const row = {
        ...procedure,
        ...slide,
        slides: [slide],
        _unwoundRowMetadata: {
          baseRowId: procedure.id,
          innerRowId: slide.id,
        },
      };

      forEach(fields, (field) => {
        const cellError = field?.getError?.({ value: get(row, field.dataKey), row, context: procedureFieldContext });

        if (cellError) {
          validationErrors.push(`${cellError}, On page ${Math.floor(rowIndex / rowsPerPage + 1)}`);
        }
      });

      rowIndex += 1;
    });
  });

  if (unmatchedRowCount) {
    validationErrors.unshift(
      unmatchedRowCount === 1
        ? '1 row has an unmatched slide identifier. Remove or replace it.'
        : `${unmatchedRowCount} rows have unmatched slide identifiers. Remove or replace these rows.`
    );
  }
  if (invalidCellRowCount) {
    validationErrors.push(
      invalidCellRowCount === 1
        ? '1 row has invalid values. Correct it in Case assignment.'
        : `${invalidCellRowCount} rows have invalid values. Correct them in Case assignment.`
    );
  }
  return Array.from(new Set(validationErrors));
};
